import { memo, useCallback, useState, useRef, useEffect } from 'react'
import classNames from 'classnames'
import { createUseStyles } from 'react-jss'
import { useGesture } from '@use-gesture/react'
import { useRaf } from '@/components/Handlers'
import useMediaQuery from '@/hooks/useMediaQuery'
import { range, lerp } from '@/utils/math'
import style from './style'

const useStyles = createUseStyles(style)

const SectorsBallon = ({
  items,
  soap = 2,
}) => {
  const classes = useStyles()
  const $root = useRef()
  const $slider = useRef()
  const viewport = useRef(0)
  const maxDistance = useRef(0)
  const x = useRef(0)
  const actualX = useRef(0)
  const newX = useRef(0)
  const lerpX = useRef(0)
  const speed = useRef(0)
  const progress = useRef(0)
  const [isDragging, setIsDragging] = useState(false)
  const { match: isMobile } = useMediaQuery('sm')

  /*------------------------------
  Gestures
  ------------------------------*/
  useGesture({
    onDrag: ({ movement: [mx], first, last }) => {
      x.current = -mx * soap
      if (first) actualX.current = newX.current

      setIsDragging(Math.abs(mx) > 10)
      if (last) setTimeout(() => setIsDragging(false), 100)
      lerpX.current = range(actualX.current - x.current, -maxDistance.current, 0)
    },
  }, {
    target: $slider,
    eventOptions: { passive: true },
    enabled: isMobile,
  })

  /*------------------------------
  Handle Resize
  ------------------------------*/
  const handleResize = useCallback(() => {
    if (isMobile) {
      viewport.current = $root.current.getBoundingClientRect().width
      maxDistance.current = $slider.current.getBoundingClientRect().width - viewport.current
    }
  }, [isMobile])

  /*------------------------------
  Init
  ------------------------------*/
  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize, false)
    return () => {
      window.removeEventListener('resize', handleResize, false)
    }
  }, [])

  /*------------------------------
  RAF
  ------------------------------*/
  useRaf(() => {
    if (isMobile) {
      newX.current = lerp(newX.current, lerpX.current, 0.08)
      speed.current = (newX.current - lerpX.current).toFixed(2)
      progress.current = (-newX.current / maxDistance.current).toFixed(3)

      if ($slider.current) $slider.current.style.transform = `translateX(${newX.current}px)`

      // CSS Vars
      $root.current.style.setProperty('--speed', speed.current)
      $root.current.style.setProperty('--speed-abs', Math.abs(speed.current))
      $root.current.style.setProperty('--progress', progress.current)
    }
  })

  return (
    <div
      className={classes.root}
      ref={$root}
    >
      <div
        className={classNames({
          [classes.list]: true,
          [classes.isDragging]: isDragging,
        })}
        ref={$slider}
      >
        {items.map((item, i) => (
          <div
            className={classNames({
              [classes.item]: true,
              [classes.itemEven]: i % 2 !== 0,
            })}
            key={i.toString()}
          >
            <div dangerouslySetInnerHTML={{ __html: item.text }} />
          </div>
        ))}
      </div>
      <div className={classes.controls}>
        <div className={classes.scrollbar}>
          <div className={classes.scrollThumbPre} style={{ flex: `0 0 ${100 / items.length}%` }} />
          <div className={classes.scrollthumb} />
        </div>
      </div>
    </div>
  )
}

export default memo(SectorsBallon)
